/**
 * Focus Management Context Provider
 * Handles focus trapping, restoration, and focusable element management
 */

/* eslint-disable react-refresh/only-export-components */
import React, { createContext, useContext, useCallback, useRef } from 'react';
import { getFocusableElements } from '@/lib/accessibility/focus-utils';

interface FocusManagement {
  trapFocus: (element: HTMLElement) => () => void;
  restoreFocus: (element: HTMLElement | null) => void;
  setFocusToFirst: (container: HTMLElement) => boolean;
  setFocusToLast: (container: HTMLElement) => boolean;
  saveFocus: () => HTMLElement | null;
}

const FocusManagementContext = createContext<FocusManagement | undefined>(undefined);

export const useFocusManagement = () => {
  const context = useContext(FocusManagementContext);
  if (context === undefined) {
    throw new Error('useFocusManagement must be used within a FocusManagementProvider');
  }
  return context;
};

interface FocusManagementProviderProps {
  children: React.ReactNode;
}

export const FocusManagementProvider: React.FC<FocusManagementProviderProps> = ({ children }) => {
  const savedFocusRef = useRef<HTMLElement | null>(null);

  const trapFocus = useCallback((element: HTMLElement) => {
    const focusableElements = getFocusableElements(element);
    const firstFocusable = focusableElements[0];
    const lastFocusable = focusableElements[focusableElements.length - 1];

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Tab') {
        if (event.shiftKey) {
          if (document.activeElement === firstFocusable) {
            event.preventDefault();
            lastFocusable?.focus();
          }
        } else {
          if (document.activeElement === lastFocusable) {
            event.preventDefault();
            firstFocusable?.focus();
          }
        }
      }
    };

    element.addEventListener('keydown', handleKeyDown);

    return () => {
      element.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  const restoreFocus = useCallback((element: HTMLElement | null) => {
    const targetElement = element || savedFocusRef.current;
    if (targetElement && document.contains(targetElement)) {
      targetElement.focus();
    }
    savedFocusRef.current = null;
  }, []);

  const setFocusToFirst = useCallback((container: HTMLElement): boolean => {
    const focusableElements = getFocusableElements(container);
    if (focusableElements.length > 0) {
      focusableElements[0].focus();
      return true;
    }
    return false;
  }, []);

  const setFocusToLast = useCallback((container: HTMLElement): boolean => {
    const focusableElements = getFocusableElements(container);
    if (focusableElements.length > 0) {
      focusableElements[focusableElements.length - 1].focus();
      return true;
    }
    return false;
  }, []);

  const saveFocus = useCallback((): HTMLElement | null => {
    const activeElement = document.activeElement as HTMLElement;
    savedFocusRef.current = activeElement;
    return activeElement;
  }, []);

  const contextValue: FocusManagement = {
    trapFocus,
    restoreFocus,
    setFocusToFirst,
    setFocusToLast,
    saveFocus,
  };

  return (
    <FocusManagementContext.Provider value={contextValue}>
      {children}
    </FocusManagementContext.Provider>
  );
};